import React, { useEffect, useState } from 'react'
import VesselDetailsHeader from 'Components/VesselDetailsHeader'
import Layout from 'HorizontalMenu/Menu'
import { RootState } from 'index'
import { useQuery } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import Select from 'react-select'
import { Navbar, Container, Row, Col, Label, Card, CardBody, Badge } from 'reactstrap'
import { customStyle } from 'shared/CommonCSS'
import { queryKeyes } from 'shared/queryKeys'
import { getVesselsAction, setOnboardedVessels, setVesselID } from 'Store/Generic/ReportingSlice'
import { Get } from 'VesselMaster/vesselMaster.hooks'
// import GHGIEGraph from './GHGIEGraph'
// import ComplianceBalanceGraph from './ComplianceBalanceGraph'
// import FuelEnergyDeveloped from './FuelEnergyDeveloped'
// import VoyagewiseCompliance from './VoyagewiseCompliance'
import { userVessels } from 'utils/auth/authUtils'
import ErrorComponent from 'Components/ErrorComponent'
import { GHGIETarget, OnboardingStatus } from 'shared/constants'
import EUMRVFuelEUTable from 'Components/EUMRVFuelEUTable'
import { useDocumentTitle } from 'Components/useDocument.hooks'
import Message from 'Components/Message'
import TableLoading from 'Components/TableLoading'
import VesselCardLoading from 'Components/vesselCardLoading'
import Skeleton from '@mui/material/Skeleton'
import { calculateNestedFieldTotal, dateTimeFormat, getDefaultVesselValue } from 'GenericForms/Helper'
import { currentYear } from 'Simulator/CIIProjection/CommonFunctions'

const FuelEUAnalysis = () => {
    /** State variables start */
    const dispatch = useDispatch();
    const { Vessels, VesselID, onboardedVessels } = useSelector(
        (state: RootState) => state.Reporting
    );
    const vessel = Vessels.find((rec: any) => rec.id === VesselID);
    const [selectedYear, setSelectedYear] = useState<number | null>(currentYear);
    let applicableVessels = userVessels();
    useDocumentTitle("FuelEU - ecoSAIL");
    /** State variables end */

    /** Queries */
    /** Last report which is sent's date */
    const {
        data: UpdatedDate,
        isLoading: UpdatedDateLoading,
        isError: UpdatedDateError,
    } = useQuery(
        [queryKeyes.vessel.LastUpdatedOn.key, VesselID],
        async () => {
            return await Get(queryKeyes.vessel.LastUpdatedOn.url(VesselID), VesselID);
        },
        { staleTime: Infinity }
    );
    /** FuelEU analysis data */
    // const {
    //     data: FuelEUData,
    //     isLoading: FuelEUDataLoading,
    //     isError: FuelEUDataError,
    // } = useQuery(
    //     [queryKeyes.vessel.FuelEUAnalysisData.key, VesselID],
    //     async () => {
    //         return await Get(queryKeyes.vessel.FuelEUAnalysisData.url(VesselID), VesselID);
    //     },
    //     { staleTime: Infinity }
    // );
    /** Last report which is sent's date */
    const {
        data: FuelEUCumulativeData,
        isLoading: FuelEUCumulativeDataLoading,
        isError: FuelEUCumulativeDataError,
    } = useQuery(
        [queryKeyes.vessel.FuelEUAnalysisCumulativeData.key, VesselID],
        async () => {
            return await Get(queryKeyes.vessel.FuelEUAnalysisCumulativeData.url(VesselID), VesselID);
        },
        { staleTime: Infinity }
    );
    /** Table data */
    const { data: eumrvData, isLoading: eumrvLoading, isError: eumrvError }:
        { data: any[], isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.Eumrv.key, VesselID, selectedYear],
            async () => {
                return await Get(queryKeyes.vessel.Eumrv.url(VesselID, selectedYear), VesselID, selectedYear);
            });
    /* -------------------- Query: Vessel Years -------------------- */
    const { data: vesselYears, isLoading: vesselYearsLoading } = useQuery(
        [queryKeyes.vessel.VesselYear.key, VesselID],
        () => Get(queryKeyes.vessel.VesselYear.url(VesselID), VesselID),
        { enabled: !!VesselID }
    );
    /** Queries end */

    /** Function for global vessel selection */
    const handleVesselSelectionChanges = (e: any) => {
        dispatch(setVesselID(e ? e?.id : null));
    };

    /** useEffect */
    /** Populate Vessels array */
    useEffect(() => {
        dispatch(getVesselsAction("vessel_master" as string));
    }, [dispatch]);

    /** Effect 2: Set onboarded vessels when Vessels data is available */
    useEffect(() => {
        if (Array.isArray(Vessels)) {
            const onboardedVessels = Vessels.filter(
                (vessel) => vessel.onboarding_status === OnboardingStatus.ONBOARDED
            );
            dispatch(setOnboardedVessels(onboardedVessels));
        }
    }, [Vessels, dispatch]);
    /** useEffect end */

    return (
        <React.Fragment>
            <Layout>
                <Navbar />
            </Layout>
            <div className="page-content">
                <Container fluid>
                    <Row className="mb-3">
                        <Col sm={4}>
                            <Label className="mb-0">Select vessel</Label>
                            <Select
                                options={
                                    onboardedVessels.filter((item: any) => item.display === true &&
                                        applicableVessels?.includes(item?.id)) as any[]
                                }
                                getOptionLabel={
                                    (option: any) => option.is_under_maintenance ?
                                        `${option?.vessel_name_type_name} (Under Maintenance)` : option?.vessel_name_type_name
                                }
                                getOptionValue={(option: any) => option.id}
                                onChange={(e: any) => handleVesselSelectionChanges(e)}
                                menuPortalTarget={document.body}
                                styles={customStyle}
                                isClearable={true}
                                placeholder="Select..."
                                defaultValue={getDefaultVesselValue(
                                    VesselID,
                                    vessel,
                                    onboardedVessels,
                                    applicableVessels
                                )}
                                noOptionsMessage={() => 'No vessels found'}
                                isOptionDisabled={(option: any) => option.is_under_maintenance}
                            />
                        </Col>
                        <Col md={2} className="mb-3">
                            <Label className="form-label mb-0">Select Year</Label>
                            <Select
                                options={vesselYears || []}
                                getOptionLabel={(o: any) => `${o.year}`}
                                getOptionValue={(o: any) => o.id}
                                isClearable
                                onChange={(e: any) => {
                                    setSelectedYear(e?.year ?? null);
                                }}
                                value={
                                    selectedYear
                                        ? vesselYears?.find((v: any) => v.year === selectedYear) ?? null
                                        : null
                                }
                                isLoading={vesselYearsLoading}
                                menuPortalTarget={document.body}
                                styles={customStyle}
                                isDisabled={!VesselID}
                            />
                        </Col>
                        <Col sm={6} className="pt-3 text-end">
                            <div className="d-inline-block">
                                <h3 className="m-0">FuelEU Maritime Assessment</h3>
                            </div>
                        </Col>
                    </Row>
                    {getDefaultVesselValue(
                        VesselID,
                        vessel,
                        onboardedVessels,
                        applicableVessels
                    ) ? (
                        <React.Fragment>
                            {(UpdatedDateLoading ||
                                //  FuelEUDataLoading ||
                                FuelEUCumulativeDataLoading || eumrvLoading) &&
                                (
                                    <>
                                        <VesselCardLoading />
                                        <div className="mt-4 mb-3">
                                            <Row className='mt-4'>
                                                <Col>
                                                    <Skeleton variant="rectangular" width="100%" height={120} />
                                                </Col>
                                                <Col>
                                                    <Skeleton variant="rectangular" width="100%" height={120} />
                                                </Col>
                                                <Col>
                                                    <Skeleton variant="rectangular" width="100%" height={120} />
                                                </Col> <Col>
                                                    <Skeleton variant="rectangular" width="100%" height={120} />
                                                </Col>
                                            </Row>
                                        </div>
                                        <TableLoading Rows={2} />
                                    </>)
                            }
                            {(UpdatedDateError ||
                                // FuelEUDataError ||
                                FuelEUCumulativeDataError || eumrvError) &&
                                <ErrorComponent message='Error loading required data!' />}
                            {!(UpdatedDateLoading ||
                                //  FuelEUDataLoading ||
                                FuelEUCumulativeDataLoading || eumrvLoading) &&
                                !(UpdatedDateError ||
                                    //  FuelEUDataError || 
                                    FuelEUCumulativeDataError || eumrvError) &&
                                <React.Fragment>
                                    <Row className='mb-2'>
                                        <Col sm={2} className="d-flex align-items-end pr-0">
                                            <p className="mb-0">
                                                Last updated on:<br />
                                                <strong>{dateTimeFormat(UpdatedDate?.date, true, false, true)}</strong>
                                            </p>
                                        </Col>
                                        <Col sm={10}>
                                            <VesselDetailsHeader />
                                        </Col>
                                    </Row>
                                    <Row className='mb-2'>
                                        <Col className='ele_row1 justify-content-center mb-2'>
                                            <Card className="dashboard-card  card-success  width-14 h-100 rounded-3">
                                                <CardBody className='px-2'>
                                                    <h5 className="card-title text-white text-center">GHGIE Target</h5>
                                                    <p className="card-text mb-0 coloured-bg-value text-center text-white">
                                                        {GHGIETarget}<br />
                                                    </p>
                                                    <p className='text-center mb-0 text-white'>gCO<sub>2eq</sub>/MJ</p>
                                                </CardBody>
                                            </Card>
                                            <Card className="dashboard-card card-primary text-white width-14 ms-2 h-100 rounded-3">
                                                <CardBody className='px-2'>
                                                    <h5 className="card-title text-white text-center">GHGIE Actual(Live)</h5>
                                                    <p className="card-text mb-0 coloured-bg-value text-center">
                                                        {FuelEUCumulativeData?.ghg_intensity?.toFixed(3)}<br />
                                                    </p>
                                                    <p className='text-center mb-0'>gCO<sub>2eq</sub>/MJ</p>
                                                </CardBody>
                                            </Card>
                                            <Card className="dashboard-card card-orange text-white width-14 ms-2 h-100 rounded-3">
                                                <CardBody className='px-2'>
                                                    <h5 className="mb-2 text-center text-white">Deviation</h5>
                                                    <p className="card-text mb-0 coloured-bg-value text-center">
                                                        {`${(parseFloat(FuelEUCumulativeData?.ghg_intensity?.toFixed(3)) / GHGIETarget) >= 1 ?
                                                            '-' : ''}
                                                ${Number.isNaN(parseFloat((parseFloat(FuelEUCumulativeData?.ghg_intensity?.toFixed(3)) / GHGIETarget)?.toFixed(2))) === true ?
                                                                '0.00' :
                                                                (parseFloat(FuelEUCumulativeData?.ghg_intensity?.toFixed(3)) / GHGIETarget)?.toFixed(2)}`}
                                                        < br />
                                                    </p>
                                                    <p className='text-center mb-0'>%</p>
                                                </CardBody>
                                            </Card>
                                            <Card className="dashboard-card card-warning text-white width-14 ms-2 h-100 rounded-3">
                                                <CardBody className="px-2">
                                                    <h5 className="card-title text-white text-center">Comp. Balance (YTD)</h5>
                                                    {(() => {
                                                        const totalBalance = calculateNestedFieldTotal(eumrvData, 'fuel_eu_record', 'Compliance_Balance');
                                                        const isDeficit = totalBalance < 0;
                                                        return (
                                                            <>
                                                                <p className="card-text mb-0 coloured-bg-value text-center">
                                                                    {totalBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                                                    <br />
                                                                </p>
                                                                <div className="text-center">
                                                                    <Badge color={isDeficit ? 'danger' : 'success'}>
                                                                        {isDeficit ? 'Deficit' : 'Surplus'}
                                                                    </Badge>
                                                                </div>
                                                            </>
                                                        );
                                                    })()}
                                                </CardBody>
                                            </Card>
                                            <Card className="dashboard-card card-danger text-white width-14 ms-2 h-100 rounded-3">
                                                <CardBody className='px-2'>
                                                    <h5 className="card-title text-white text-center">Penalty (YTD)</h5>
                                                    <p className="card-text mb-0 coloured-bg-value text-center">
                                                        {calculateNestedFieldTotal(eumrvData, 'fuel_eu_record', 'penalty')}  <br />
                                                    </p>
                                                    <p className='text-center mb-0'>EUR</p>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>
                                    {/* <Row className='mb-3'>
                                        <Col sm={4}>
                                            <h6 className='mb-0'>Live GIGHE Graph</h6>
                                            <GHGIEGraph fuelEUData={FuelEUData} />
                                        </Col>
                                        <Col sm={4}>
                                            <h6 className='mb-0'>Live Compliance Balance Graph</h6>
                                            <ComplianceBalanceGraph graphData={eumrvData} />
                                        </Col>
                                        <Col sm={2}>
                                            <h6 className='mb-0'>Fuelwise contribution to energy developed</h6>
                                            <FuelEnergyDeveloped fuelEUData={FuelEUCumulativeData} />
                                        </Col>
                                        <Col sm={2}>
                                            <h6 className='mb-0'>Voyagewise compliance</h6>
                                            <VoyagewiseCompliance pieChartData={eumrvData} />
                                        </Col>
                                    </Row> */}
                                    <Row className='mt-2'>
                                        <EUMRVFuelEUTable
                                            eumrvData={eumrvData}
                                            operation='FuelEU'
                                        />
                                    </Row>
                                </React.Fragment>
                            }
                        </React.Fragment>
                    ) : (
                        <Message message="Please select a vessel to proceed." type="warning" width="365px" />
                    )}
                </Container>
            </div>
        </React.Fragment>
    )
}

export default FuelEUAnalysis